import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV } from "../lib/config";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const light = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <>
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-ink/5 bg-bg-primary/70 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className={`font-serif text-2xl tracking-tight ${light ? "text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" : "text-ink"}`}>Victoria Fuentes</span>
          <span className={`mt-1 text-[0.6rem] uppercase tracking-[0.24em] ${light ? "text-white/85 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]" : "text-sage-dark"}`}>Psicóloga Sanitaria · Málaga</span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  data-testid="nav-services-trigger"
                  className={`flex items-center gap-1 text-sm transition-colors duration-300 ${light ? "text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.6)] hover:text-white/80" : "text-ink hover:text-sage-dark"}`}
                >
                  {item.label}
                  <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25 }}
                      className="absolute left-1/2 top-full w-64 -translate-x-1/2 pt-4"
                    >
                      <div className="rounded-3xl border border-ink/5 bg-bg-primary/95 p-3 shadow-xl backdrop-blur-xl">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-service-${c.to}`}
                            className="block rounded-2xl px-4 py-3 text-sm text-ink-soft transition-colors duration-200 hover:bg-bg-sage hover:text-ink"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to}`}
                className={`text-sm transition-colors duration-300 ${light ? "text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.6)] hover:text-white/80" : "text-ink hover:text-sage-dark"}`}
              >
                {item.label}
              </Link>
            )
          )}
          <Link
            to="/contacto"
            data-testid="nav-cta-button"
            className={`rounded-full px-6 py-3 text-sm font-medium transition-colors duration-300 ${light ? "bg-white text-ink hover:bg-white/90" : "bg-ink text-bg-primary hover:bg-sage-dark"}`}
          >
            Pide tu primera cita
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menú"
          data-testid="mobile-menu-open"
          className="lg:hidden"
        >
          <Menu className={`h-7 w-7 ${light ? "text-white" : "text-ink"}`} strokeWidth={1.5} />
        </button>
      </nav>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-bg-primary px-6 py-6 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-2xl text-ink">Victoria Fuentes</span>
              <button onClick={() => setMobileOpen(false)} aria-label="Cerrar menú" data-testid="mobile-menu-close">
                <X className="h-7 w-7 text-ink" strokeWidth={1.5} />
              </button>
            </div>
            <div className="mt-12 flex flex-col gap-6">
              {NAV.map((item) => (
                <div key={item.label}>
                  {item.children ? (
                    <span className="font-serif text-3xl text-ink" data-testid="mobile-heading-servicios">
                      {item.label}
                    </span>
                  ) : (
                    <Link to={item.to} className="font-serif text-3xl text-ink" data-testid={`mobile-link-${item.to}`}>
                      {item.label}
                    </Link>
                  )}
                  {item.children && (
                    <div className="mt-3 flex flex-col gap-2 pl-4">
                      {item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="text-base text-ink-soft" data-testid={`mobile-service-${c.to}`}>
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/contacto"
                className="mt-4 rounded-full bg-ink px-6 py-4 text-center text-base font-medium text-bg-primary"
                data-testid="mobile-cta-button"
              >
                Pide tu primera cita
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
